import React from 'react';
import { Shield, AlertTriangle, CheckCircle, XCircle, Clock, Mail, Wrench } from 'lucide-react';
import { useColorTheme } from '../contexts/ColorThemeContext';
import { cn } from '../util/designUtil';

const RefundPolicy: React.FC = () => {
  const { theme } = useColorTheme();

  const SectionIcon = ({ icon: Icon, className }: { icon: any; className?: string }) => (
    <Icon className={cn("h-5 w-5", theme.secondary, className)} />
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-4xl mx-auto px-4 py-4 sm:py-8">
        {/* Title Section */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">Refund Policy</h1>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
            <Shield className="h-4 w-4" />
            <span>Last Updated: March 9, 2026</span>
          </div>
          <p className="mt-4 text-base sm:text-lg text-gray-700 max-w-2xl mx-auto">
            At Nexbii, we aim to provide a fair experience for both fans and creators. This Refund Policy
            explains when refunds may be granted for bookings made on the platform.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">

          {/* Section 1: Requesting a Refund */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Mail} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">1. Requesting a Refund</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">
                All refund requests must be initiated by the user by contacting Nexbii support at:
              </p>
              <div className="flex items-center gap-2 mb-4">
                <Mail className="h-4 w-4 text-gray-500" />
                <a href="mailto:info@nexbii.com" className={cn("font-medium", theme.secondary)}>
                  info@nexbii.com
                </a>
              </div>
              <p>Our team will review the request and determine eligibility according to the terms below.</p>
            </div>
          </section>

          {/* Section 2: Creator No-Show */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={AlertTriangle} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">2. Creator No-Show</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">
                If a creator does not join the scheduled video call within a reasonable time, the user may request a refund.
              </p>
              <p className="mb-4">If approved, Nexbii may issue a refund either:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>to the original payment method, or</li>
                <li>in the form of in-app tokens credited to the user's Nexbii account</li>
              </ul>
              <div className={cn("p-4 rounded-lg border-l-4", theme.accent)}>
                <p className="font-medium">The refund method will be determined by Nexbii depending on the circumstances.</p>
              </div>
            </div>
          </section>

          {/* Section 3: User Cancellation */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={CheckCircle} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">3. User Cancellation</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800">
                  Users may cancel a scheduled call within the allowed cancellation window (24 hours prior to the
                  scheduled call) and the refund will automatically be transferred as in-app tokens instantaneously.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4: User No-Show */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={XCircle} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">4. User No-Show</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800">
                  If a user fails to attend or join the scheduled call at the booked time, the booking will
                  not be eligible for a refund.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5: Late Cancellations */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Clock} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">5. Late Cancellations</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800">
                  If a booking is cancelled less than 24 hours before the scheduled call, the booking will
                  not normally be eligible for a refund.
                </p>
              </div>
            </div>
          </section>

          {/* Section 6: Completed Calls */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={CheckCircle} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">6. Completed Calls</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">
                If the creator joins the scheduled call, the service will generally be considered delivered.
              </p>
              <p className="mb-4">
                In cases where the call ends earlier than expected or the creator leaves the call before the
                scheduled duration for reasons such as inappropriate or inconsiderate behaviour, users may provide
                feedback or rate the creator through the Nexbii platform.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800">
                  As the creator successfully joined the scheduled session, refunds will generally not be issued
                  in these circumstances, regardless of whether the full scheduled duration was completed.
                </p>
              </div>
            </div>
          </section>

          {/* Section 7: Technical Issues */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Wrench} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">7. Technical Issues</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">
                If a call cannot take place due to verified technical issues on the Nexbii platform, users
                may contact support to request a refund review.
              </p>
              <div className={cn("p-4 rounded-lg border-l-4", theme.accent)}>
                <p className="font-medium">
                  Please contact us at{' '}
                  <a href="mailto:info@nexbii.com" className={cn("underline", theme.secondary)}>
                    info@nexbii.com
                  </a>{' '}
                  with details of the issue so our team can investigate.
                </p>
              </div>
            </div>
          </section>

          {/* Section 8: Processing Time */}
          <section className="p-4 sm:p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Clock} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">8. Processing Time</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">
                Approved refunds will typically be processed within a reasonable timeframe after review by
                our support team.
              </p>
              <div className="flex flex-col gap-3 mt-4">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-500" />
                  <a href="mailto:info@nexbii.com" className={cn("font-medium", theme.secondary)}>
                    info@nexbii.com
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>© 2025 Nexbii. All rights reserved.</p>
          <p className="mt-2">Last updated: March 9, 2026</p>
        </div>
      </main>
    </div>
  );
};

export default RefundPolicy;
